// frontend/src/modules/invoicing/totals.ts
//
// EXACT mirror of api/modules/invoicing/totals.ts (the single source of money
// math) so editor previews match what the server stores. Keep the two in sync.

export interface TotalsItemInput {
  quantity: number;
  unit_price: number;
  tax_rate?: number;
  discount_percent?: number | null;
}

export interface InvoiceDiscount {
  type: 'percent' | 'fixed';
  value: number;
}

export interface ComputedTotals {
  lineSubtotals: number[];
  lineTaxes: number[];
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  total: number;
}

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function computeTotals(
  items: TotalsItemInput[],
  opts: { invoiceDiscount?: InvoiceDiscount | null; taxRateOverride?: number | null } = {},
): ComputedTotals {
  const override = opts.taxRateOverride;

  const prelim = items.map((it) => {
    const base = round2((Number(it.quantity) || 0) * (Number(it.unit_price) || 0));
    const dp = it.discount_percent == null ? 0 : Number(it.discount_percent) || 0;
    const lineDiscount = dp ? round2(base * (dp / 100)) : 0;
    const subtotal = round2(base - lineDiscount);
    const rate = override != null ? Number(override) : Number(it.tax_rate ?? 0) || 0;
    return { subtotal, rate };
  });

  const subtotal = round2(prelim.reduce((s, r) => s + r.subtotal, 0));

  let discountTotal = 0;
  const d = opts.invoiceDiscount;
  if (d && Number(d.value) > 0 && subtotal > 0) {
    discountTotal = d.type === 'percent'
      ? round2(subtotal * (Number(d.value) / 100))
      : round2(Math.min(Number(d.value), subtotal));
  }

  let allocated = 0;
  const lineSubtotals: number[] = [];
  const lineTaxes: number[] = [];
  prelim.forEach((r, i) => {
    let share = 0;
    if (discountTotal > 0) {
      share = i === prelim.length - 1
        ? round2(discountTotal - allocated)
        : round2(discountTotal * (r.subtotal / subtotal));
      allocated = round2(allocated + share);
    }
    const taxable = round2(r.subtotal - share);
    lineSubtotals.push(r.subtotal);
    lineTaxes.push(round2(taxable * (r.rate / 100)));
  });

  const taxTotal = round2(lineTaxes.reduce((s, t) => s + t, 0));
  const total = round2(subtotal - discountTotal + taxTotal);

  return { lineSubtotals, lineTaxes, subtotal, discountTotal, taxTotal, total };
}
